/**
 * Sales Overview — Decision-Grade Sales Dashboard
 *
 * The single source of truth for business health. Single page, single API call
 * (GET /api/dashboard/business-pulse — backend route name preserved as a
 * stable contract). Designed to answer in under 30 seconds:
 *   1. Is the business healthy?
 *   2. Is growth real or fragile?
 *   3. Where are we leaking money?
 *   4. Are we dependent on risky channels?
 *   5. What should we fix first?
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { dashboardApi, salesApi } from '../lib/api';
import { toast } from 'sonner';
import {
  ChartBar, TrendUp, TrendDown, Lightning, Warning, ShieldCheck, Coin,
  ArrowUpRight, ArrowDownRight, ArrowRight, ArrowLeft, X as XIcon, Calendar, Clock,
  Funnel, Target, CaretDown, Info, Question, Lightbulb,
  Wrench,
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  Cell, ReferenceLine, PieChart, Pie, RadialBarChart, RadialBar,
} from 'recharts';
import DateRangeFilter from '../components/DateRangeFilter';
import DeltaPill from '../components/DeltaPill';
import {
  isValidISO, fmtDDMMYYYY, resolveInitialRange,
} from '../lib/dateRange';

// ──────────────────────────────────────────────────────────────────
// Utilities
// ──────────────────────────────────────────────────────────────────

const SEVERITY = {
  good:    { ring: 'ring-emerald-500/40', text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', dot: 'bg-emerald-400', glow: 'shadow-[0_0_40px_rgba(34,197,94,0.10)]', label: 'Good' },
  warning: { ring: 'ring-amber-500/40',   text: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   dot: 'bg-amber-400',   glow: 'shadow-[0_0_40px_rgba(234,179,8,0.10)]',  label: 'Watch' },
  risk:    { ring: 'ring-red-500/40',     text: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/30',     dot: 'bg-red-400',     glow: 'shadow-[0_0_40px_rgba(239,68,68,0.10)]',  label: 'Risk' },
  neutral: { ring: 'ring-cyan-500/30',    text: 'text-cyan-400',    bg: 'bg-cyan-500/10',    border: 'border-white/10',       dot: 'bg-cyan-400',    glow: 'shadow-[0_0_40px_rgba(56,189,248,0.06)]', label: 'Neutral' },
};

const DONUT_PALETTE = ['#10B981', '#06B6D4', '#A78BFA', '#F59E0B', '#EC4899', '#64748B'];
// Stable color map per Phase 8 — UPI green, CASH neutral, CARD blue, Swiggy
// orange tint, Zomato red tint. Sort order on the donut is by value DESC,
// but each bucket keeps its colour regardless of position.
const PAYMENT_COLOR_MAP = {
  'UPI':              '#10B981', // emerald
  'CASH':             '#94A3B8', // neutral slate
  'CARD':             '#3B82F6', // blue
  'SWIGGY DELIVERY':  '#F97316', // orange-500
  'SWIGGY DINING':    '#FB923C', // orange-400 (lighter — dine-in tier)
  'ZOMATO DELIVERY':  '#EF4444', // red-500
  'ZOMATO DINING':    '#FB7185', // rose-400 (lighter — dine-in tier)
  'UNCLASSIFIED':     '#A1A1AA', // zinc-400 — only ever rendered if data drift
};

// Range presets, helpers, and the popover live in `lib/dateRange.js` +
// `components/DateRangeFilter.jsx`. This file consumes them via that
// shared module so Sales Overview and Sales Explorer stay in lockstep.

// ──────────────────────────────────────────────────────────────────
// Data hook
// ──────────────────────────────────────────────────────────────────

function useBusinessPulse({ outletId, dateFrom, dateTo, compareFrom, compareTo, comparisonMode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = { outlet_id: outletId, date_from: dateFrom, date_to: dateTo };
    if (comparisonMode) params.comparison_mode = comparisonMode;
    if (compareFrom) params.compare_from = compareFrom;
    if (compareTo) params.compare_to = compareTo;
    dashboardApi
      .getBusinessPulse(params)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e.message || 'Failed to load Sales Overview');
          toast.error('Could not load Sales Overview');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [outletId, dateFrom, dateTo, compareFrom, compareTo, comparisonMode]);

  return { data, loading, error };
}

// ──────────────────────────────────────────────────────────────────
// Shared shell
// ──────────────────────────────────────────────────────────────────

function PulseCard({ children, className = '', glow = 'neutral', testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border border-white/10 bg-[linear-gradient(180deg,rgba(15,23,42,0.92),rgba(3,7,18,0.92))] backdrop-blur-xl transition-all duration-200 hover:-translate-y-0.5 hover:border-white/20 ${SEVERITY[glow].glow} ${className}`}
    >
      {children}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Header
// ──────────────────────────────────────────────────────────────────

function PulseHeader({
  onRangeChange, dateFrom, dateTo, lastSync,
  comparisonMode, onComparisonModeChange,
}) {
  return (
    <header className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 animate-fade-in" data-testid="pulse-header">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D4FF00 0%, #A6CC00 100%)' }}>
          <span className="text-zinc-950 font-mono font-bold text-lg">1</span>
        </div>
        <div>
          <h1 className="text-2xl lg:text-3xl font-light tracking-tight text-white" data-testid="pulse-title">
            Sales Overview
          </h1>
          <p className="text-xs text-zinc-500 font-mono mt-0.5" data-testid="pulse-subtitle">
            Real-time sales health & decisions · {fmtDDMMYYYY(dateFrom)} → {fmtDDMMYYYY(dateTo)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap" data-testid="pulse-controls">
        <DateRangeFilter
          value={{ from: dateFrom, to: dateTo }}
          onChange={onRangeChange}
          testIdPrefix="pulse-range"
        />
        {lastSync && (
          <div className="hidden md:flex items-center gap-1.5 text-[10px] text-zinc-500 font-mono pl-2">
            <Clock size={11} /> {new Date(lastSync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
        {onComparisonModeChange && (
          <div className="flex items-center gap-1.5 ml-0 md:ml-2" data-testid="pulse-comparison-mode-wrap">
            <span className="hidden md:inline text-[10px] uppercase tracking-widest font-mono text-zinc-500">Compare</span>
            <select
              value={comparisonMode || 'auto'}
              onChange={(e) => onComparisonModeChange(e.target.value)}
              data-testid="pulse-comparison-mode-select"
              className="text-xs font-mono px-2 py-1.5 rounded-md border bg-[#0F1115] border-white/10 text-zinc-100 hover:border-white/25"
              title="Comparison period used for deltas, Health and scoring"
            >
              <option value="auto">Auto (recommended)</option>
              <option value="previous_period">Previous equal period</option>
              <option value="previous_month_like_for_like">Same days last month</option>
              <option value="previous_month_full">Full previous month</option>
              <option value="previous_year_like_for_like">Same period last year</option>
              <option value="custom">Custom</option>
              <option value="none">No comparison</option>
            </select>
          </div>
        )}
      </div>
    </header>
  );
}

// ──────────────────────────────────────────────────────────────────
// Top strip — Health card + 4 metric cards
// ──────────────────────────────────────────────────────────────────

function BusinessHealthCard({ health }) {
  const score = health?.score ?? 0;
  const sev = score >= 75 ? 'good' : score >= 50 ? 'warning' : 'risk';
  // Three-tier user-facing label per product directive (override the 5-tier
  // backend label without changing the API contract).
  const displayLabel = score >= 70 ? 'Healthy' : score >= 40 ? 'Attention Needed' : 'Critical';
  const ring = sev === 'good' ? '#10B981' : sev === 'warning' ? '#F59E0B' : '#EF4444';
  // Animate score from 0 → score
  const [animScore, setAnimScore] = useState(0);
  useEffect(() => {
    let raf, start;
    const dur = 900;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / dur, 1);
      setAnimScore(Math.round(score * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [score]);

  return (
    <PulseCard glow={sev} className="p-5 h-full" testid="pulse-health-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Business Health</p>
          <div className="flex items-end gap-3">
            <span className="text-5xl font-mono font-light text-white tabular-nums" data-testid="pulse-health-score">{animScore}</span>
            <span className={`text-sm font-mono ${SEVERITY[sev].text} mb-1.5`} data-testid="pulse-health-label">{displayLabel}</span>
          </div>
          <p className="text-xs text-zinc-400 mt-1">
            {(health?.delta_points || 0) >= 0 ? '+' : ''}{health?.delta_points || 0} pts vs last period
          </p>
        </div>
        <div className="relative w-24 h-24">
          <svg viewBox="0 0 36 36" className="-rotate-90">
            <path d="M18 2 a 16 16 0 0 1 0 32 a 16 16 0 0 1 0 -32" fill="none" stroke="#27272A" strokeWidth="3" />
            <path
              d="M18 2 a 16 16 0 0 1 0 32 a 16 16 0 0 1 0 -32"
              fill="none" stroke={ring} strokeWidth="3" strokeLinecap="round"
              strokeDasharray={`${(animScore / 100) * 100.53} 100.53`}
              style={{ transition: 'stroke-dasharray 800ms cubic-bezier(0.22, 1, 0.36, 1)' }}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-mono text-zinc-500">/ 100</span>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-2 gap-2" data-testid="pulse-health-components">
        {(health?.components || []).map((c) => {
          const cs = c.score >= 75 ? 'good' : c.score >= 50 ? 'warning' : 'risk';
          return (
            <div key={c.label} className="flex items-center justify-between text-[11px] py-1">
              <span className="text-zinc-400 truncate">{c.label}</span>
              <span className={`font-mono ${SEVERITY[cs].text} flex items-center gap-1`}>
                <span className={`w-1.5 h-1.5 rounded-full ${SEVERITY[cs].dot}`} /> {c.status}
              </span>
            </div>
          );
        })}
      </div>
    </PulseCard>
  );
}

function MetricCard({ metric, accent = 'neutral', testid }) {
  if (!metric) return null;
  const dir = metric.direction || 'flat';
  const isMuted = dir === 'muted' || metric.delta_pct === null;
  const Icon = isMuted ? Info : dir === 'up' ? ArrowUpRight : dir === 'down' ? ArrowDownRight : ArrowRight;
  const dCol = isMuted
    ? 'text-zinc-500'
    : dir === 'up' ? 'text-emerald-400' : dir === 'down' ? 'text-red-400' : 'text-zinc-500';
  const infoCompare = metric.informational_compare;
  return (
    <PulseCard glow={accent} className="p-4 h-full flex flex-col justify-between" testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500">{metric.label}</p>
      <div className="mt-2">
        <p className="text-2xl font-mono font-light text-white tabular-nums" data-testid={`${testid}-value`}>{metric.display}</p>
        {isMuted ? (
          infoCompare ? (
            <p
              className="text-[11px] font-mono mt-1 flex items-center gap-1 text-zinc-500 italic"
              data-testid={`${testid}-info-compare`}
              title="Unequal comparison — informational only, not used for scoring"
            >
              <Icon size={11} /> vs {infoCompare.display} <span className="text-zinc-600">· informational only</span>
            </p>
          ) : metric.date ? (
            <p className="text-xs text-zinc-500 font-mono mt-1">on {metric.date}</p>
          ) : null
        ) : metric.delta_pct !== undefined ? (
          <p className={`text-xs font-mono mt-1 flex items-center gap-1 ${dCol}`} data-testid={`${testid}-delta`}>
            <Icon size={12} /> {metric.delta_pct > 0 ? '+' : ''}{metric.delta_pct}% vs prior
          </p>
        ) : metric.date ? (
          <p className="text-xs text-zinc-500 font-mono mt-1">on {metric.date}</p>
        ) : null}
      </div>
    </PulseCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Sales trend
// ──────────────────────────────────────────────────────────────────

function SalesTrendCard({ trend }) {
  const points = trend?.points || [];
  const best = trend?.best_day;
  const low = trend?.lowest_day;
  return (
    <PulseCard className="p-5 h-full" testid="pulse-sales-trend">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-medium text-zinc-200">Sales Trend</h3>
          <p className="text-[11px] text-zinc-500 font-mono mt-0.5">Daily net sales · best & lowest day flagged</p>
        </div>
        <div className="flex gap-3 text-[10px] font-mono">
          {best && (
            <span className="flex items-center gap-1 text-emerald-400" data-testid="pulse-best-day">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Best {best.display_date} · {best.display}
            </span>
          )}
          {low && (
            <span className="flex items-center gap-1 text-red-400" data-testid="pulse-lowest-day">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" /> Low {low.display_date} · {low.display}
            </span>
          )}
        </div>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
            <XAxis dataKey="display_date" stroke="#52525B" fontSize={10} interval="preserveStartEnd" />
            <YAxis stroke="#52525B" fontSize={10} tickFormatter={(v) => v >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : v} />
            <Tooltip
              cursor={{ fill: 'rgba(212,255,0,0.05)' }}
              content={<SalesTrendTooltip />}
            />
            <Bar dataKey="net_sales" radius={[4, 4, 0, 0]} animationDuration={700}>
              {points.map((p, i) => {
                const isBest = best && p.date === best.date;
                const isLow = low && p.date === low.date;
                return (
                  <Cell
                    key={i}
                    fill={isBest ? '#10B981' : isLow ? '#EF4444' : '#06B6D4'}
                    fillOpacity={isBest || isLow ? 1 : 0.78}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </PulseCard>
  );
}

// Sales trend tooltip — renders the full daily P&L derivation on hover
// so every bar explains how its number was built. Lines map 1:1 to the
// canonical aggregation in services/business_pulse.py daily $group.
//   gross − discounts = net  +  service_charge  +  tax  =  total_collected
// We only render rows whose underlying field is present on the point —
// missing ≠ zero. (e.g. a day with no service_charge from the POS shows
// no service charge row, instead of falsely claiming ₹0.)
const _fmtINR = (v) => {
  if (v == null) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const _fmtFullDate = (iso, fallback) => {
  if (!iso) return fallback || '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return fallback || iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

function SalesTrendTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;

  // Mini P&L rows — present only when the field exists on the point.
  // Order: gross → −discounts → =net → +service → +tax → =total.
  const rows = [];
  const has = (k) => Object.prototype.hasOwnProperty.call(p, k) && p[k] != null;

  if (has('gross_sales')) {
    rows.push({ label: 'Gross Sales', value: p.gross_sales, op: '', testid: 'tt-gross' });
  }
  if (has('discounts')) {
    rows.push({ label: 'Discounts', value: p.discounts, op: '−', testid: 'tt-discounts' });
  }
  rows.push({
    label: 'Net Sales', value: p.net_sales, op: '=',
    sep: rows.length > 0, testid: 'tt-net',
  });
  if (has('service_charge')) {
    rows.push({ label: 'Service Charge', value: p.service_charge, op: '+', testid: 'tt-service' });
  }
  if (has('taxes')) {
    rows.push({ label: 'Tax', value: p.taxes, op: '+', testid: 'tt-tax' });
  }
  // Adjustments — the residual that accounts for per-order
  // round_off + tip. Surfaced only when materially non-zero so the
  // accounting reconciles exactly. "Materially" = >₹1 OR >0.1% of net.
  if (has('final_total') && has('net_sales')) {
    const svc = has('service_charge') ? p.service_charge : 0;
    const tax = has('taxes') ? p.taxes : 0;
    const adj = p.final_total - p.net_sales - svc - tax;
    const threshold = Math.max(1, Math.abs(p.net_sales) * 0.001);
    if (Math.abs(adj) > threshold) {
      rows.push({
        label: 'Adjustments',
        value: adj,
        op: adj >= 0 ? '+' : '−',
        // For negative adjustments we render the operator and the
        // unsigned absolute amount — like a P&L. Tip + round_off
        // typically aggregate positive on Rista.
        valueOverride: adj < 0 ? Math.abs(adj) : null,
        testid: 'tt-adjustments',
      });
    }
  }
  if (has('final_total')) {
    rows.push({
      label: 'Total Collected', value: p.final_total, op: '=',
      sep: true, bold: true, testid: 'tt-total',
    });
  }

  return (
    <div
      className="rounded-lg border border-white/10 bg-[#0B0F14] px-4 py-3 shadow-xl"
      data-testid="pulse-sales-trend-tooltip"
    >
      <div className="text-[12px] font-medium text-zinc-100 mb-2">
        {_fmtFullDate(p.date, p.display_date)}
      </div>
      <div className="text-[11px] text-zinc-500 font-mono mb-2">
        {p.orders} {p.orders === 1 ? 'order' : 'orders'}
      </div>
      <div className="space-y-0.5 font-mono text-[12px] tabular-nums">
        {rows.map((r) => (
          <div
            key={r.label}
            className={`grid grid-cols-[14px_1fr_auto] gap-2 items-baseline ${r.sep ? 'pt-1 border-t border-white/10 mt-1' : ''} ${r.bold ? 'text-zinc-100 font-semibold' : 'text-zinc-300'}`}
            data-testid={r.testid}
          >
            <span className="text-zinc-500">{r.op}</span>
            <span>{r.label}</span>
            <span>{_fmtINR(r.valueOverride != null ? r.valueOverride : r.value) ?? '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Key Insights panel
// ──────────────────────────────────────────────────────────────────

function FixThisFirstCard({ insight }) {
  if (!insight) return null;
  const sevMap = { high: 'risk', medium: 'warning', low: 'good' };
  const s = SEVERITY[sevMap[insight.severity] || 'neutral'];
  return (
    <div
      data-testid="pulse-fix-this-first"
      className={`relative rounded-lg border ${s.border} bg-gradient-to-br from-[#D4FF00]/[0.07] to-${insight.severity === 'high' ? 'red' : 'amber'}-500/[0.08] p-3 mb-3 hover:translate-x-0.5 transition-transform`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Target size={12} weight="fill" className="text-[#D4FF00]" />
        <p className="text-[9px] uppercase tracking-widest text-[#D4FF00] font-mono">Fix this first</p>
      </div>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-white font-medium leading-snug" data-testid="pulse-fix-this-first-title">{insight.title}</p>
        <p className={`text-sm font-mono font-medium ${s.text} shrink-0`}>{insight.metric}</p>
      </div>
      <p className="text-[11px] text-zinc-400 mt-1 leading-snug">{insight.body}</p>
      <p className="text-[11px] text-zinc-200 mt-1.5 flex items-start gap-1">
        <ArrowRight size={11} className="mt-0.5 shrink-0" /> {insight.action}
      </p>
    </div>
  );
}

function KeyInsightsPanel({ insights }) {
  const sevMap = { high: 'risk', medium: 'warning', low: 'good' };
  const list = insights || [];
  // Highest impact_score wins the "Fix This First" slot. Tie-break by index.
  const ranked = [...list].sort((a, b) => (b.impact_score || 0) - (a.impact_score || 0));
  const fixThis = ranked.find((i) => (i.impact_score || 0) > 0) || ranked[0];
  const rest = list.filter((i) => i !== fixThis);

  return (
    <PulseCard className="p-5 h-full" testid="pulse-key-insights">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-zinc-200 flex items-center gap-2">
          <Lightning size={14} className="text-[#D4FF00]" weight="fill" /> What Needs Attention
        </h3>
        <span className="text-[10px] font-mono text-zinc-500">{list.length} active</span>
      </div>
      {list.length === 0 && (
        <p className="text-xs text-zinc-500 italic" data-testid="pulse-insights-empty">No urgent attention items — business looks calm.</p>
      )}
      {fixThis && <FixThisFirstCard insight={fixThis} />}
      <div className="space-y-2.5">
        {rest.map((i, idx) => {
          const s = SEVERITY[sevMap[i.severity] || 'neutral'];
          return (
            <div
              key={idx}
              data-testid={`pulse-insight-${idx}`}
              className={`relative pl-3 pr-3 py-2.5 rounded-lg border ${s.border} ${s.bg} hover:translate-x-0.5 transition-transform cursor-pointer`}
            >
              <span className={`absolute left-0 top-2 bottom-2 w-0.5 rounded ${s.dot}`} />
              <div className="flex items-start justify-between gap-2">
                <p className={`text-[10px] font-mono uppercase tracking-wide ${s.text}`}>{i.severity}</p>
                <p className={`text-sm font-mono font-medium ${s.text}`}>{i.metric}</p>
              </div>
              <p className="text-xs text-zinc-100 font-medium mt-1">{i.title}</p>
              <p className="text-[11px] text-zinc-400 mt-0.5 leading-snug">{i.body}</p>
              <p className="text-[11px] text-zinc-300 mt-1.5 flex items-start gap-1">
                <ArrowRight size={11} className="mt-0.5 shrink-0" /> {i.action}
              </p>
            </div>
          );
        })}
      </div>
    </PulseCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Long-Term Signals
// ──────────────────────────────────────────────────────────────────

function HistoryReadinessBanner({ history }) {
  if (!history || history.long_term_ready) return null;
  return (
    <div
      className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 px-3 py-2.5 text-[11px] font-mono flex items-start gap-2"
      data-testid="pulse-history-readiness-banner"
    >
      <Calendar size={14} className="text-cyan-400 mt-0.5 shrink-0" />
      <div className="flex-1">
        <p className="text-cyan-200">
          Long-term intelligence will improve once {history.long_term_threshold_months}+ months of sales history is available.
        </p>
        <p className="text-cyan-200/60 mt-0.5">
          Current insights are based on the selected period only.
          {history.first_sales_date && ` History so far: ${fmtDDMMYYYY(history.first_sales_date)} → ${fmtDDMMYYYY(history.last_sales_date)} (${history.available_months} month${history.available_months === 1 ? '' : 's'}).`}
        </p>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Long-Term Signals — bento, infographic-first.
// Phase 7.8 rebuild (2026-06-02). The old card swallowed `className`
// in destructuring, so the responsive col-span classes never reached
// the DOM and every card collapsed to ~1/12 width. Beyond fixing that,
// we redesigned for "owner-readable in under 4 seconds":
//   - mini 3-month bar chart at the top (visual first)
//   - large delta + headline below it
//   - one-line interpretation + one-line action
//   - long explanation hidden behind "Explain signal"
// ──────────────────────────────────────────────────────────────────

const LT_TONE = {
  good:    { bar: 'bg-emerald-400', barFaint: 'bg-emerald-500/20', pill: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',  num: 'text-emerald-300', ring: 'border-emerald-500/25' },
  warning: { bar: 'bg-amber-400',   barFaint: 'bg-amber-500/20',   pill: 'bg-amber-500/15 text-amber-300 border-amber-500/30',        num: 'text-amber-300',   ring: 'border-amber-500/25' },
  risk:    { bar: 'bg-red-400',     barFaint: 'bg-red-500/20',     pill: 'bg-red-500/15 text-red-300 border-red-500/30',              num: 'text-red-300',     ring: 'border-red-500/25' },
  neutral: { bar: 'bg-cyan-400',    barFaint: 'bg-cyan-500/20',    pill: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',           num: 'text-cyan-300',    ring: 'border-cyan-500/25' },
};
const LT_STATUS_LABEL = { good: 'Good', warning: 'Watch', risk: 'Risk', neutral: 'Info' };

function _fmtINRCompact(v) {
  const n = Number(v) || 0;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000)     return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n)}`;
}

function MiniSeriesChart({ series, status }) {
  // Compact 3-month bar chart. The newest month (rightmost) is the
  // dominant bar — accent-coloured for the card's status. Older
  // months are tinted-down for the "you got here from here" read.
  if (!series || series.length === 0) return null;
  const tone = LT_TONE[status] || LT_TONE.neutral;
  const max = Math.max(...series.map((s) => Math.abs(s.value)), 0.0001);
  const fmt = (s) => {
    if (s.kind === 'currency') return _fmtINRCompact(s.value);
    if (s.kind === 'percent')  return `${s.value.toFixed(1)}%`;
    return `${Math.round(s.value)}`;
  };
  return (
    <div className="flex items-end justify-between gap-3 h-20 mb-3" data-testid="lt-mini-chart">
      {series.map((s, i) => {
        const h = Math.max(8, Math.round((Math.abs(s.value) / max) * 64));
        const isLatest = i === series.length - 1;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
            <span className={`text-[9.5px] font-mono tabular-nums ${isLatest ? tone.num : 'text-zinc-500'} truncate`}>
              {fmt(s)}
            </span>
            <div
              className={`w-full rounded-t ${isLatest ? tone.bar : tone.barFaint} transition-all`}
              style={{ height: `${h}px` }}
              data-testid={`lt-bar-${i}`}
            />
            <span className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono truncate w-full text-center">
              {s.label.split(' ')[0]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function LongTermSignalCard({ signal, testid, className = '' }) {
  const [explainOpen, setExplainOpen] = useState(false);
  if (!signal) return null;
  const tone = LT_TONE[signal.status] || LT_TONE.neutral;
  const label = LT_STATUS_LABEL[signal.status] || 'Info';
  return (
    <PulseCard glow={signal.status} className={`p-5 ${tone.ring} ${className}`} testid={testid}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <h4 className="text-[12px] font-medium text-zinc-100 leading-snug">{signal.title}</h4>
        <span
          className={`shrink-0 px-2 py-0.5 rounded-full text-[9.5px] font-mono uppercase tracking-widest border ${tone.pill}`}
          data-testid={`${testid}-status`}
        >
          {label}
        </span>
      </div>

      <MiniSeriesChart series={signal.series} status={signal.status} />

      <div className="flex items-baseline gap-2 mb-1">
        <span className={`text-[28px] font-light tabular-nums ${tone.num}`} data-testid={`${testid}-primary`}>
          {signal.primary_value}
        </span>
        <span className="text-[10.5px] text-zinc-500 truncate">{signal.primary_label}</span>
      </div>

      <p className="text-[12.5px] text-zinc-200 leading-snug mt-2" data-testid={`${testid}-headline`}>
        {signal.headline || signal.interpretation}
      </p>

      <div className="mt-3 pt-2.5 border-t border-white/5 flex items-start gap-2">
        <ArrowRight size={11} className={`mt-0.5 shrink-0 ${tone.num}`} />
        <span className="text-[11.5px] text-zinc-300 leading-snug" data-testid={`${testid}-action`}>
          {signal.recommended_action}
        </span>
      </div>

      <button
        type="button"
        onClick={() => setExplainOpen((v) => !v)}
        aria-expanded={explainOpen}
        className="mt-2.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition-colors"
        data-testid={`${testid}-explain-toggle`}
      >
        {explainOpen ? '− Hide details' : '+ Explain signal'}
      </button>

      <AnimatePresence initial={false}>
        {explainOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="mt-2 pt-2 border-t border-white/5 space-y-1.5" data-testid={`${testid}-explain-body`}>
              <p className="text-[11px] text-zinc-400 leading-snug">{signal.interpretation}</p>
              <p className="text-[10.5px] text-zinc-500 font-mono">{signal.secondary}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </PulseCard>
  );
}

function LongTermSignalsSection({ signals }) {
  if (!signals || signals.length === 0) return null;
  return (
    <section data-testid="pulse-long-term-section" className="space-y-3">
      <div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-medium text-zinc-200 flex items-center gap-2">
            <TrendUp size={14} className="text-cyan-400" /> Long-Term Signals
          </h2>
          <span className="text-[10px] font-mono text-zinc-500" data-testid="pulse-long-term-subtitle">
            3-month structural trends · independent of the selected date filter
          </span>
        </div>
        <p className="text-[10.5px] font-mono text-zinc-600 mt-1">
          Compared month-over-month across the trailing 3 months.
        </p>
      </div>
      <div className="grid grid-cols-12 gap-4">
        {signals.map((s, i) => (
          <LongTermSignalCard
            key={i}
            signal={s}
            testid={`pulse-long-term-${i}`}
            className="col-span-12 md:col-span-6 xl:col-span-4"
          />
        ))}
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────
// Signal Grid (6 cards)
// ──────────────────────────────────────────────────────────────────

// Map of signal keys → severity-based ring color used for the expanded panel
const SIGNAL_RING_HEX = {
  good: '#10B981', warning: '#F59E0B', risk: '#EF4444', neutral: '#06B6D4',
};

// ──────────────────────────────────────────────────────────────────
// Trend chip — shows {direction, delta, magnitude} per signal.
// ↑ worsening (red) · ↓ improving (green) · → stable (neutral)
// Colour is direction-driven, NOT metric-direction.
// ──────────────────────────────────────────────────────────────────
const TREND_STYLE = {
  worsening: {
    icon: TrendUp,
    cls: 'text-red-400 bg-red-500/10 border-red-500/30',
    label: 'Worsening',
  },
  improving: {
    icon: TrendDown,
    cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    label: 'Improving',
  },
  stable: {
    icon: ArrowRight,
    cls: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/30',
    label: 'Stable',
  },
};

function TrendChip({ trend, testid }) {
  if (!trend || !trend.direction) return null;
  const t = TREND_STYLE[trend.direction] || TREND_STYLE.stable;
  const Icon = t.icon;
  const delta = typeof trend.delta === 'number' ? trend.delta : 0;
  const sign = delta > 0 ? '+' : '';
  // Hide delta on stable to keep chip minimal — user only needs to see "stable".
  const tooltip = trend.direction === 'stable'
    ? `Stable vs prior period (Δ ${sign}${delta.toFixed(1)})`
    : `${t.label} vs prior period · Δ ${sign}${delta.toFixed(1)} · ${trend.magnitude}`;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase border ${t.cls}`}
      title={tooltip}
      data-testid={testid}
      data-trend-direction={trend.direction}
      data-trend-magnitude={trend.magnitude}
    >
      <Icon size={9} weight="bold" />
      {trend.direction === 'stable' ? 'Stable' : `${sign}${delta.toFixed(1)}`}
    </span>
  );
}

function SignalCard({ signal, signalKey, isExpanded, onClick, className = '', testid }) {
  if (!signal) return null;
  const s = SEVERITY[signal.severity] || SEVERITY.neutral;
  const score = signal.score ?? 50;
  const ring = SIGNAL_RING_HEX[signal.severity] || SIGNAL_RING_HEX.neutral;
  return (
    <PulseCard
      glow={signal.severity || 'neutral'}
      className={`p-4 cursor-pointer select-none ${isExpanded ? 'ring-1 ring-white/15 -translate-y-0.5' : ''} ${className}`}
      testid={testid}
    >
      <button
        type="button"
        onClick={() => onClick && onClick(signalKey)}
        aria-expanded={isExpanded}
        aria-controls={`${testid}-explanation`}
        className="w-full text-left"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <h4 className="text-[11px] uppercase tracking-widest text-zinc-400 font-mono">{signal.title}</h4>
          <div className="flex items-center gap-1.5 shrink-0">
            <TrendChip trend={signal.trend} testid={`${testid}-trend`} />
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase ${s.bg} ${s.text} border ${s.border}`}>
              {signal.status}
            </span>
          </div>
        </div>
        <div className="flex items-end gap-3">
          <div className="flex-1 min-w-0">
            <p className={`text-2xl font-mono font-light tabular-nums ${s.text}`} data-testid={`${testid}-primary`}>{signal.primary_value}</p>
            <p className="text-[10px] text-zinc-500 mt-0.5 truncate">{signal.primary_label}</p>
          </div>
          <div className="relative w-12 h-12 shrink-0">
            <svg viewBox="0 0 36 36" className="-rotate-90">
              <circle cx="18" cy="18" r="15" fill="none" stroke="#27272A" strokeWidth="3" />
              <circle
                cx="18" cy="18" r="15" fill="none"
                stroke={ring}
                strokeWidth="3" strokeLinecap="round"
                strokeDasharray={`${(score / 100) * 94.25} 94.25`}
                style={{ transition: 'stroke-dasharray 700ms ease-out' }}
              />
            </svg>
            <span className={`absolute inset-0 flex items-center justify-center text-[9px] font-mono ${s.text}`}>{score}</span>
          </div>
        </div>
        <p className="text-[11px] text-zinc-400 mt-2 leading-snug border-t border-white/5 pt-2">{signal.secondary}</p>
        <p className="text-[11px] text-zinc-200 mt-1.5">{signal.interpretation}</p>
        <p className="text-[9px] uppercase tracking-widest text-zinc-500 mt-2.5 font-mono">What should I do</p>
        <p className="text-[11px] text-zinc-300 mt-1 flex items-start gap-1 opacity-90">
          <ArrowRight size={11} className="mt-0.5 shrink-0" /> <span className="leading-snug">{signal.recommended_action}</span>
        </p>
        {/* Persistent footer CTA */}
        <div
          className={`mt-3 pt-2.5 border-t border-white/5 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest transition-colors ${isExpanded ? s.text : 'text-zinc-500 hover:text-zinc-300'}`}
          data-testid={`${testid}-cta`}
        >
          <span>{isExpanded ? 'Hide explanation' : 'Explain signal'}</span>
          <CaretDown
            size={11}
            className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>
    </PulseCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Signal explanation panel — full-row, single-expansion
// ──────────────────────────────────────────────────────────────────

const EXPLAIN_SECTIONS = [
  { key: 'what_this_says',     icon: Info,     label: 'What this says'      },
  { key: 'what_it_means',      icon: Question, label: 'What it means'       },
  { key: 'why_this_matters',   icon: Lightbulb,label: 'Why this matters'    },
  { key: 'what_you_should_do', icon: Wrench,   label: 'What you should do'  },
];

function SignalExplanationPanel({ signal, signalKey, onClose, onViewInDiscounts, testid }) {
  if (!signal || !signal.explanation) return null;
  const s = SEVERITY[signal.severity] || SEVERITY.neutral;
  const exp = signal.explanation;
  const showDiscountsCta = signalKey === 'revenue_leak';
  return (
    <motion.div
      initial={{ opacity: 0, y: -8, height: 0 }}
      animate={{ opacity: 1, y: 0, height: 'auto' }}
      exit={{ opacity: 0, y: -8, height: 0 }}
      transition={{ duration: 0.24, ease: [0.22, 0.61, 0.36, 1] }}
      className="overflow-hidden col-span-12"
      id={testid}
      data-testid={testid}
    >
      <PulseCard
        glow={signal.severity || 'neutral'}
        className={`p-5 mt-1 border-l-2 ${s.border}`}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className={`text-[10px] uppercase tracking-widest font-mono ${s.text}`}>
              {signal.title} — {signal.status}
            </p>
            <p className={`text-2xl font-mono font-light tabular-nums ${s.text} mt-1`}>{signal.primary_value}</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">{signal.primary_label}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testid}-close`}
            className="text-zinc-500 hover:text-zinc-200 p-1 transition-colors"
            aria-label="Close explanation"
          >
            <XIcon size={14} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {EXPLAIN_SECTIONS.map(({ key, icon: Icon, label }) => {
            const lines = exp[key] || [];
            if (!lines.length) return null;
            return (
              <div key={key} data-testid={`${testid}-${key}`}>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-zinc-400 mb-2">
                  <Icon size={11} className="text-zinc-500" /> {label}
                </div>
                <ul className="space-y-1.5">
                  {lines.map((l, i) => (
                    <li key={i} className="text-[12px] text-zinc-200 leading-snug">{l}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {exp.bottom_line && (
          <div className={`mt-4 pt-3 border-t border-white/5 flex items-start gap-2`}>
            <Target size={12} weight="fill" className={`${s.text} mt-1 shrink-0`} />
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Bottom line</p>
              <p className={`text-sm font-medium mt-1 ${s.text}`} data-testid={`${testid}-bottom-line`}>{exp.bottom_line}</p>
            </div>
          </div>
        )}

        {showDiscountsCta && (
          <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between gap-3">
            <div className="text-[11px] text-zinc-400 leading-relaxed">
              See where this leak lives — by partner, server, day, and reason.
            </div>
            <button
              type="button"
              onClick={onViewInDiscounts}
              data-testid={`${testid}-view-in-discounts`}
              className="text-[11px] font-mono px-3 py-1.5 rounded border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/15 transition-colors inline-flex items-center gap-1.5"
            >
              View in Discounts <ArrowRight size={12} />
            </button>
          </div>
        )}
      </PulseCard>
    </motion.div>
  );
}

function ExpandableSignalGrid({ signals, dateRange, autoExpandKey, contextFrom, onClearContext }) {
  const [expandedKey, setExpandedKey] = useState(null);
  const navigate = useNavigate();
  const order = ['order_quality', 'revenue_stability', 'growth_quality', 'revenue_leak', 'channel_dependence', 'menu_power'];
  const handleClick = (k) => setExpandedKey((prev) => (prev === k ? null : k));
  const expanded = expandedKey ? signals?.[expandedKey] : null;
  const handleViewInDiscounts = () => {
    const qs = new URLSearchParams();
    qs.set('from', 'revenue_leak');
    if (dateRange?.date_from) qs.set('date_from', dateRange.date_from);
    if (dateRange?.date_to) qs.set('date_to', dateRange.date_to);
    navigate(`/discounts?${qs.toString()}`);
  };

  // Honour deep-link from Discounts FTF — auto-expand the signal once data arrives.
  // Track which deep-link key we've already auto-opened so user can collapse without re-firing.
  const [appliedAutoKey, setAppliedAutoKey] = useState(null);
  useEffect(() => {
    if (!signals || !autoExpandKey || appliedAutoKey === autoExpandKey) return;
    if (!order.includes(autoExpandKey)) return;
    setExpandedKey(autoExpandKey);
    setAppliedAutoKey(autoExpandKey);
    // Soft scroll the grid into view after the panel has had a moment to mount.
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="pulse-signal-${autoExpandKey.replace(/_/g, '-')}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signals, autoExpandKey]);

  return (
    <section className="space-y-3" data-testid="pulse-signal-grid-wrap">
      {contextFrom === 'discounts_fix_this_first' && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22 }}
          className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 flex items-start gap-3"
          data-testid="pulse-context-banner"
        >
          <div className="shrink-0 w-7 h-7 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
            <Lightning size={14} weight="fill" className="text-red-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.2em] text-red-400/80 font-mono">Linked from Discounts</div>
            <div className="text-sm font-medium text-zinc-100 mt-0.5">You are here because Discounts identified a root cause</div>
            <div className="text-xs text-zinc-400 mt-1">
              The Revenue Leak signal below has been expanded for context.
              Use it to set the priority for what to fix this week.
            </div>
          </div>
          <button
            onClick={() => navigate(-1)}
            data-testid="pulse-banner-back-btn"
            className="text-[11px] font-mono text-zinc-300 hover:text-white border border-white/10 rounded-md px-2 py-1 flex items-center gap-1"
          >
            <ArrowLeft size={12} /> Back to Discounts
          </button>
          <button
            onClick={onClearContext}
            data-testid="pulse-banner-dismiss-btn"
            className="text-zinc-500 hover:text-zinc-200 text-lg leading-none"
            aria-label="Dismiss"
          >×</button>
        </motion.div>
      )}
      <section className="grid grid-cols-12 gap-3" data-testid="pulse-signal-grid">
        {order.map((k) => (
          <SignalCard
            key={k}
            signal={signals?.[k]}
            signalKey={k}
            isExpanded={expandedKey === k}
            onClick={handleClick}
            className="col-span-12 md:col-span-6 xl:col-span-2"
            testid={`pulse-signal-${k.replace(/_/g, '-')}`}
          />
        ))}
      </section>
      <AnimatePresence initial={false}>
        {expanded && (
          <SignalExplanationPanel
            key={expandedKey}
            signal={expanded}
            signalKey={expandedKey}
            onClose={() => setExpandedKey(null)}
            onViewInDiscounts={handleViewInDiscounts}
            testid={`pulse-signal-explanation-${expandedKey.replace(/_/g, '-')}`}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────
// Weekly Change Ribbon — top-of-page "what changed since last week"
// Sticky on scroll. Each line is clickable → auto-expands its signal.
// Strict rules: max 3 lines · no charts · no jargon · no noise.
// ──────────────────────────────────────────────────────────────────

const WC_DIR_STYLE = {
  worsening: 'text-red-300',
  improving: 'text-emerald-300',
  stable:    'text-zinc-400',
};

const WC_ARROW = (dir) => (dir === 'worsening' ? '↑' : dir === 'improving' ? '↓' : '→');

function WeeklyChangeRibbon({ data, onItemClick }) {
  if (!data) return null;
  const items = data.items || [];
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="sticky top-0 z-30 -mx-4 lg:-mx-6 px-4 lg:px-6 py-2 bg-[#070B10]/95 backdrop-blur-sm border-b border-white/5"
      data-testid="pulse-weekly-change-ribbon"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <Lightning size={14} weight="fill" className="text-[#D4FF00]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-400 flex items-center gap-2">
            <span>What changed</span>
            <span className="text-zinc-600">·</span>
            <span className="text-zinc-500">{data.period_label}</span>
          </div>
          {items.length > 0 ? (
            <ul className="mt-1.5 flex flex-col md:flex-row md:items-center md:gap-x-5 md:gap-y-1 gap-1 flex-wrap" data-testid="pulse-weekly-change-list">
              {items.map((it) => (
                <li key={it.signal} className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => onItemClick && onItemClick(it)}
                    data-testid={`pulse-weekly-change-item-${it.signal.replace(/_/g, '-')}`}
                    className="text-[12px] md:text-[13px] text-left hover:bg-white/5 rounded-md px-1.5 py-0.5 transition-colors flex items-center gap-1.5 group"
                    title={`Priority ${it.priority_score} · ${it.severity}`}
                  >
                    <span className={`font-mono text-[11px] ${WC_DIR_STYLE[it.direction] || ''}`}>{WC_ARROW(it.direction)}</span>
                    <span className="text-zinc-100">{it.message}</span>
                    <ArrowRight size={11} className="text-zinc-600 group-hover:text-zinc-300 transition-colors" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1.5 text-[12px] text-zinc-400" data-testid="pulse-weekly-change-empty">
              {data.no_movement_message || 'No major movement since last week.'}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}



// ──────────────────────────────────────────────────────────────────
// What You Should Fix First — full-width prominent card
// ──────────────────────────────────────────────────────────────────

function WhatYouShouldFixFirstCard({ insights }) {
  const list = insights || [];
  if (!list.length) return null;
  // Highest impact_score wins. Tie-breaker: higher severity weight.
  const sevWeight = { high: 3, medium: 2, low: 1 };
  const top = [...list].sort((a, b) => {
    const s = (b.impact_score || 0) - (a.impact_score || 0);
    if (s !== 0) return s;
    return (sevWeight[b.severity] || 0) - (sevWeight[a.severity] || 0);
  })[0];
  if (!top || (top.impact_score || 0) === 0) return null; // only positive insights = nothing to fix

  const sevMap = { high: 'risk', medium: 'warning', low: 'good' };
  const s = SEVERITY[sevMap[top.severity] || 'neutral'];
  return (
    <PulseCard
      glow={sevMap[top.severity] || 'warning'}
      className="p-5 relative overflow-hidden"
      testid="pulse-what-fix-first"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4FF00]/40 to-transparent" />
      <div className="flex items-center gap-2 mb-3">
        <Target size={14} weight="fill" className="text-[#D4FF00]" />
        <p className="text-[10px] uppercase tracking-widest font-mono text-[#D4FF00]">What you should fix first</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-start">
        <div className="md:col-span-5">
          <h2 className="text-xl md:text-2xl font-light text-white leading-tight" data-testid="pulse-what-fix-first-title">
            {top.title}
          </h2>
          <p className={`text-3xl font-mono font-light tabular-nums mt-3 ${s.text}`}>{top.metric}</p>
          <p className="text-[11px] text-zinc-500 mt-1">{top.body}</p>
        </div>
        <div className="md:col-span-4">
          <p className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-1.5">Why this is first</p>
          <p className="text-[12px] text-zinc-200 leading-snug">{top.body}</p>
          <p className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mt-3 mb-1.5">What to do this week</p>
          <p className="text-[12px] text-zinc-200 leading-snug" data-testid="pulse-what-fix-first-action">{top.action}</p>
        </div>
        <div className="md:col-span-3">
          <p className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-1.5">Expected impact</p>
          <p className="text-[12px] text-zinc-300 leading-snug" data-testid="pulse-what-fix-first-impact">
            {top.expected_impact || 'Resolving this protects margin and decision quality this period.'}
          </p>
        </div>
      </div>
    </PulseCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Bottom analytics row
// ──────────────────────────────────────────────────────────────────

// Format an INR amount compactly: ₹6.76L / ₹58.3K / ₹842.
function formatInr(v) {
  const n = Number(v) || 0;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_00_000) return `${sign}₹${(abs / 1_00_000).toFixed(2)}L`;
  if (abs >= 1_000)    return `${sign}₹${(abs / 1_000).toFixed(1)}K`;
  return `${sign}₹${Math.round(abs).toLocaleString('en-IN')}`;
}

function DonutCard({ title, data, palette, colorMap, valueKey = 'pct', testid, footnote, showAllRows = false, showAbsValue = false, headerExtra }) {
  const total = data?.reduce((a, b) => a + (b.value || 0), 0) || 0;
  const colorFor = (row, idx) => {
    if (colorMap && row?.label && colorMap[row.label]) return colorMap[row.label];
    return palette[idx % palette.length];
  };
  const visible = showAllRows ? (data || []) : (data || []).slice(0, 4);
  return (
    <PulseCard className="p-4 h-full" testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[11px] uppercase tracking-widest text-zinc-400 font-mono">{title}</h4>
        {headerExtra}
      </div>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data || []} dataKey={valueKey} nameKey="label" innerRadius={42} outerRadius={66} paddingAngle={2} animationDuration={700}>
              {(data || []).map((row, i) => <Cell key={i} fill={colorFor(row, i)} />)}
            </Pie>
            <Tooltip
              contentStyle={{ backgroundColor: '#0B0F14', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 11 }}
              formatter={(v, _n, p) => [`${p.payload.label}: ${v}%  (₹${Number(p.payload.value).toLocaleString('en-IN')})`, '']}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-1 mt-2">
        {visible.map((d, i) => (
          <div key={d.label} className="flex items-center justify-between text-[11px] gap-2" data-testid={`${testid}-row-${d.label.replace(/\s+/g,'-').toLowerCase()}`}>
            <span className="flex items-center gap-1.5 text-zinc-300 truncate min-w-0">
              <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: colorFor(d, i) }} />
              <span className="truncate">{d.label}</span>
            </span>
            {showAbsValue ? (
              <span className="font-mono text-zinc-200 shrink-0 tabular-nums">
                {formatInr(d.value)} <span className="text-zinc-500">({d[valueKey]}%)</span>
              </span>
            ) : (
              <span className="font-mono text-zinc-200 shrink-0 tabular-nums">{d[valueKey]}%</span>
            )}
          </div>
        ))}
      </div>
      {footnote && <p className="text-[10px] text-emerald-400 font-mono mt-2 pt-2 border-t border-white/5">{footnote}</p>}
    </PulseCard>
  );
}

function RevenueBreakdownCard({ data }) {
  const max = Math.max(...(data || []).map((d) => Math.abs(d.value || 0))) || 1;
  return (
    <PulseCard className="p-4 h-full" testid="pulse-revenue-breakdown">
      <h4 className="text-[11px] uppercase tracking-widest text-zinc-400 font-mono mb-3">Revenue Breakdown</h4>
      <div className="space-y-2">
        {(data || []).map((d) => {
          const pct = (Math.abs(d.value) / max) * 100;
          const col = d.type === 'negative' ? 'bg-red-500/70' : d.type === 'positive' ? 'bg-emerald-500/70' : 'bg-cyan-500/60';
          return (
            <div key={d.label} className="space-y-1" data-testid={`pulse-rb-${d.label.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-zinc-300">{d.label}</span>
                <span className={`font-mono ${d.type === 'negative' ? 'text-red-400' : 'text-zinc-200'}`}>{d.display}</span>
              </div>
              <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                <div className={`h-full ${col} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </PulseCard>
  );
}

function DayOfWeekCard({ data }) {
  const colorByStatus = {
    best: '#10B981', average: '#06B6D4', weak: '#F59E0B', no_data: '#52525B',
  };
  const weekendAvg = (data || []).filter((d) => ['Sat', 'Sun'].includes(d.day)).reduce((a, b) => a + b.net_sales, 0) / 2;
  const weekdayAvg = (data || []).filter((d) => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(d.day)).reduce((a, b) => a + b.net_sales, 0) / 5;
  const totalAvg = weekendAvg + weekdayAvg * 2.5;
  const weekendShare = totalAvg > 0 ? (weekendAvg * 2 / totalAvg * 100) : 0;
  return (
    <PulseCard className="p-4 h-full" testid="pulse-day-of-week">
      <h4 className="text-[11px] uppercase tracking-widest text-zinc-400 font-mono mb-2">Day of Week</h4>
      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data || []} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
            <XAxis dataKey="day" stroke="#52525B" fontSize={10} interval={0} tickMargin={2} />
            <YAxis hide />
            <Tooltip
              contentStyle={{ backgroundColor: '#0B0F14', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 11 }}
              formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, 'Avg net']}
            />
            <Bar dataKey="net_sales" radius={[3, 3, 0, 0]} animationDuration={700}>
              {(data || []).map((d, i) => <Cell key={i} fill={colorByStatus[d.status] || '#06B6D4'} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {weekendShare > 0 && (
        <p className="text-[10px] font-mono text-zinc-400 mt-2 pt-2 border-t border-white/5">
          Weekends drive <span className="text-emerald-400">{weekendShare.toFixed(0)}%</span> of revenue
        </p>
      )}
    </PulseCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Top items this period (qty + period-over-period delta)
// ──────────────────────────────────────────────────────────────────
//
// Small strip surfacing the top-3 items by quantity for the active
// `(dateFrom, dateTo)` range. Each row shows the quantity sold and a
// shared `<DeltaPill>` comparing to the previous equivalent period.
//
// Backend contract:
//   GET /api/sales/items/top?limit=3&sort_by=quantity&include_comparison=true
//   → { items: [{ item_name, quantity_sold, quantity_change_pct,
//                 quantity_change_direction, previous_quantity, ... }],
//       comparison: { label, previous_from, previous_to } }
//
// Revenue delta is deliberately NOT shown — the backend currently
// only computes quantity deltas. Mixing a qty% next to a revenue
// figure would be misleading, so the Top Item (Revenue) story stays
// out of this strip until a revenue-delta contract lands.
//
function TopItemsThisPeriodCard({ outletId, dateFrom, dateTo }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(false);
    salesApi.getTopItems({
      date_from: dateFrom,
      date_to: dateTo,
      sort_by: 'quantity',
      limit: 3,
      include_comparison: true,
      ...(outletId ? { outlet_id: outletId } : {}),
    }).then((res) => {
      if (cancelled) return;
      setItems(res.data?.items || []);
      setComparison(res.data?.comparison || null);
    }).catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [outletId, dateFrom, dateTo]);

  // Deep-link an item-row click to Sales Explorer Items → detail
  // drawer with the same date range. See SalesExplorerPage.jsx for
  // the URL contract: `?tab=items&item=<normalized>&date_from=&date_to=`.
  // We use `item_name_normalized` (already the backend's canonical key)
  // so a refresh on Explorer rehydrates the same drawer.
  const openInExplorer = (it) => {
    if (!it?.item_name_normalized) return;
    const qs = new URLSearchParams({
      tab: 'items',
      item: it.item_name_normalized,
      date_from: dateFrom,
      date_to: dateTo,
    });
    navigate(`/explorer?${qs.toString()}`);
  };

  return (
    <PulseCard testid="pulse-top-items-card" className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Lightning size={14} className="text-[#D4FF00]" weight="duotone" />
          <h3 className="text-[12px] uppercase tracking-widest font-mono text-zinc-300">
            Top Items This Period
          </h3>
        </div>
        {comparison && (
          <span
            className="text-[10px] font-mono text-zinc-500"
            data-testid="pulse-top-items-compare-label"
            title={`${comparison.label} (${comparison.previous_from} → ${comparison.previous_to})`}
          >{comparison.label}</span>
        )}
      </div>
      {loading && (
        <div className="space-y-2" data-testid="pulse-top-items-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-7 bg-white/[0.03] rounded animate-pulse" />
          ))}
        </div>
      )}
      {!loading && (error || !items?.length) && (
        <p className="text-[12px] text-zinc-500 py-3" data-testid="pulse-top-items-empty">
          No items in this range
        </p>
      )}
      {!loading && !error && items?.length > 0 && (
        <ol className="space-y-0.5" data-testid="pulse-top-items-list">
          {items.map((it, idx) => (
            <li
              key={it.item_name_normalized || idx}
              role="button"
              tabIndex={0}
              onClick={() => openInExplorer(it)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openInExplorer(it);
                }
              }}
              className="group flex items-center gap-2 py-1.5 px-1.5 -mx-1.5 rounded
                         cursor-pointer transition-colors hover:bg-white/[0.04]
                         focus:outline-none focus:ring-1 focus:ring-[#D4FF00]/30"
              title={`Open ${it.item_name} in Sales Explorer`}
              data-testid={`pulse-top-item-${idx}`}
              data-item-normalized={it.item_name_normalized}
            >
              <span className="text-[10px] font-mono text-zinc-600 w-4 tabular-nums">
                {idx + 1}.
              </span>
              <span className="text-[13px] text-zinc-200 truncate flex-1 group-hover:text-white">
                {it.item_name}
              </span>
              <span className="text-[12px] font-mono text-zinc-400 tabular-nums">
                {it.quantity_sold}
              </span>
              <DeltaPill
                direction={it.quantity_change_direction}
                pct={it.quantity_change_pct}
                title={comparison && (it.previous_quantity != null
                  ? `${comparison.label} · prev qty ${it.previous_quantity}`
                  : comparison.label)}
                testId={`pulse-top-item-delta-${idx}`}
              />
              <span
                className="text-[10px] font-mono text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity ml-0.5"
                aria-hidden="true"
                data-testid={`pulse-top-item-view-affordance-${idx}`}
              >→</span>
            </li>
          ))}
        </ol>
      )}
    </PulseCard>
  );
}


// ──────────────────────────────────────────────────────────────────
// Footer (data confidence)
// ──────────────────────────────────────────────────────────────────

function DataConfidenceFooter({ confidence }) {
  if (!confidence) return null;
  const score = confidence.confidence_score || 0;
  const sev = score >= 80 ? 'good' : score >= 60 ? 'warning' : 'risk';
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-[11px] font-mono pt-3 border-t border-white/5" data-testid="pulse-confidence-footer">
      <div className="flex items-center gap-2">
        <ShieldCheck size={14} className={SEVERITY[sev].text} />
        <span className="text-zinc-400">Data Confidence:</span>
        <span className={`${SEVERITY[sev].text} font-medium`} data-testid="pulse-confidence-score">{score} · {confidence.label}</span>
      </div>
      <div className="flex items-center gap-3 text-zinc-500">
        <span>{confidence.canonical_rows?.toLocaleString()} canonical rows</span>
        <span>·</span>
        <span>{confidence.validation_errors} validation errors</span>
        <span>·</span>
        <span>last sync {new Date(confidence.last_sync_at).toLocaleString([], { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
    </div>
  );
}

function LowConfidenceBanner({ score }) {
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] font-mono flex items-center gap-2" data-testid="pulse-low-confidence-banner">
      <Warning size={14} className="text-amber-400" weight="fill" />
      <span className="text-amber-200">Data confidence is below decision threshold ({score}). Use this view for investigation, not final decisions.</span>
    </div>
  );
}

function ComparisonBanner({ period }) {
  if (!period) return null;
  const fairness = period.fairness || {};
  const isPartial = period.current?.is_partial || period.partial;
  const message = fairness.message;
  const basis = fairness.basis;

  // Colour logic:
  //   unequal_period  → amber (unfair; scoring blanked)
  //   no_comparison   → muted zinc (no signal)
  //   partial+fair    → 1OS yellow (like-for-like on partial)
  //   complete+fair   → muted emerald (standard comparison info)
  let tone = 'emerald';
  if (basis === 'unequal_period') tone = 'amber';
  else if (basis === 'no_comparison') tone = 'zinc';
  else if (isPartial) tone = 'lime';

  const toneClasses = {
    lime:    { border: 'border-[#D4FF00]/30', bg: 'bg-[#D4FF00]/5',    icon: 'text-[#D4FF00]',  text: 'text-[#E7FF8C]' },
    amber:   { border: 'border-amber-500/30', bg: 'bg-amber-500/10',    icon: 'text-amber-300',  text: 'text-amber-100' },
    zinc:    { border: 'border-white/10',     bg: 'bg-white/[0.02]',    icon: 'text-zinc-400',   text: 'text-zinc-300' },
    emerald: { border: 'border-emerald-500/20', bg: 'bg-emerald-500/5', icon: 'text-emerald-300', text: 'text-emerald-100' },
  }[tone];

  if (!message) return null;

  return (
    <div
      className={`rounded-lg border ${toneClasses.border} ${toneClasses.bg} px-3 py-2 text-[11px] font-mono flex items-center gap-2 flex-wrap`}
      data-testid="pulse-comparison-banner"
      data-fairness={fairness.is_fair ? 'fair' : 'unfair'}
      data-basis={basis || 'none'}
    >
      <Warning size={14} className={toneClasses.icon} weight={isPartial || !fairness.is_fair ? 'fill' : 'regular'} />
      <span className={toneClasses.text}>{message}</span>
      {!fairness.is_fair && basis === 'unequal_period' && (
        <span
          className="ml-auto px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/15 text-amber-200 text-[10px] uppercase tracking-wider"
          data-testid="pulse-unfair-chip"
        >
          Scoring uses selected period only
        </span>
      )}
    </div>
  );
}

// Legacy alias — preserved so any dormant reference keeps working.
function PartialPeriodBanner({ period }) {
  return <ComparisonBanner period={period} />;
}

// ──────────────────────────────────────────────────────────────────
// Loading + error skeletons
// ──────────────────────────────────────────────────────────────────

function PulseSkeleton() {
  const Box = ({ h = 'h-24', cols = 'col-span-3' }) => (
    <div className={`${cols} ${h} rounded-2xl bg-zinc-900/40 border border-white/5 animate-pulse`} />
  );
  return (
    <div className="min-h-screen bg-[#070B10] text-white p-6 space-y-5" data-testid="pulse-skeleton">
      <Box h="h-12" cols="col-span-12 max-w-md" />
      <div className="grid grid-cols-12 gap-4">
        <Box cols="col-span-12 xl:col-span-3" h="h-44" />
        <Box cols="col-span-6 xl:col-span-2" h="h-44" />
        <Box cols="col-span-6 xl:col-span-2" h="h-44" />
        <Box cols="col-span-6 xl:col-span-2" h="h-44" />
        <Box cols="col-span-6 xl:col-span-3" h="h-44" />
      </div>
      <div className="grid grid-cols-12 gap-4">
        <Box cols="col-span-12 xl:col-span-9" h="h-72" />
        <Box cols="col-span-12 xl:col-span-3" h="h-72" />
      </div>
      <div className="grid grid-cols-12 gap-4">
        {Array.from({ length: 6 }).map((_, i) => <Box key={i} cols="col-span-6 xl:col-span-2" h="h-40" />)}
      </div>
    </div>
  );
}

function PulseError({ error, onRetry }) {
  return (
    <div className="min-h-screen bg-[#070B10] text-white flex items-center justify-center p-6" data-testid="pulse-error">
      <PulseCard className="p-6 max-w-md text-center">
        <Warning size={36} className="mx-auto text-red-400 mb-3" weight="fill" />
        <h2 className="text-lg font-medium text-white mb-1">Could not load Sales Overview</h2>
        <p className="text-xs text-zinc-400 font-mono mb-4">{error}</p>
        <button onClick={onRetry} className="px-4 py-2 bg-[#D4FF00]/15 text-[#D4FF00] border border-[#D4FF00]/30 rounded-lg text-xs font-mono hover:bg-[#D4FF00]/25 transition" data-testid="pulse-retry-btn">
          Retry
        </button>
      </PulseCard>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// MAIN
// ──────────────────────────────────────────────────────────────────

export default function BusinessPulseDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Document title — first impression, command-center framing.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Sales Overview · 1OS';
    return () => { document.title = prev; };
  }, []);

  // ── URL-driven date state ──────────────────────────────────────
  // The shared contract: date_from / date_to URL params drive state.
  // Default landing range is THIS MONTH when params are missing or
  // invalid (centralised in `resolveInitialRange()` so every page
  // applies the same default).
  const urlCFrom = searchParams.get('compare_from');
  const urlCTo = searchParams.get('compare_to');
  const initial = resolveInitialRange(searchParams);
  const initialMode = searchParams.get('comparison_mode') || 'auto';
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo,   setDateTo]   = useState(initial.to);
  const [comparisonMode, setComparisonMode] = useState(initialMode);

  // Sync URL whenever the applied range changes.
  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    sp.set('date_from', dateFrom);
    sp.set('date_to', dateTo);
    if (comparisonMode && comparisonMode !== 'auto') sp.set('comparison_mode', comparisonMode);
    else sp.delete('comparison_mode');
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, comparisonMode]);

  const handleRangeChange = (from, to) => {
    if (!isValidISO(from) || !isValidISO(to)) {
      toast.error('Invalid dates'); return;
    }
    setDateFrom(from); setDateTo(to);
  };

  const { data, loading, error } = useBusinessPulse({
    dateFrom, dateTo, comparisonMode,
    compareFrom: urlCFrom, compareTo: urlCTo,
  });

  if (loading) return <PulseSkeleton />;
  if (error) return <PulseError error={error} onRetry={() => window.location.reload()} />;
  if (!data) return null;

  const noData = (data.metrics?.orders?.value || 0) === 0;
  const lowConf = (data.data_confidence?.confidence_score || 0) < 70;

  return (
    <div className="min-h-screen bg-[#070B10] text-white" data-testid="pulse-dashboard">
      <div className="px-4 lg:px-6 py-5 space-y-5 max-w-[1700px] mx-auto">
        {/* Weekly Change Ribbon — sticky, top of page. Clicking a line
            sets ?signal=<key> which triggers in-page auto-expand of that
            signal's explanation panel via ExpandableSignalGrid. */}
        <WeeklyChangeRibbon
          data={data.weekly_changes}
          onItemClick={(it) => {
            const next = new URLSearchParams(searchParams);
            next.set('signal', it.signal);
            next.set('from', 'weekly_change');
            setSearchParams(next, { replace: false });
            // The ExpandableSignalGrid useEffect will handle expansion + scroll.
          }}
        />
        <PulseHeader
          onRangeChange={handleRangeChange}
          dateFrom={data.period?.date_from || dateFrom}
          dateTo={data.period?.date_to || dateTo}
          lastSync={data.data_confidence?.last_sync_at}
          comparisonMode={comparisonMode}
          onComparisonModeChange={setComparisonMode}
        />
        {lowConf && <LowConfidenceBanner score={data.data_confidence?.confidence_score} />}
        <ComparisonBanner period={data.period} />
        <HistoryReadinessBanner history={data.history_context} />

        {noData ? (
          <PulseCard className="p-10 text-center" testid="pulse-no-data">
            <Coin size={36} className="mx-auto text-zinc-600 mb-3" />
            <p className="text-sm text-zinc-300 font-mono">No canonical sales found for this period.</p>
            <p className="text-[11px] text-zinc-500 font-mono mt-1">Check source activation or upload status.</p>
          </PulseCard>
        ) : (
          <>
            {/* Top strip */}
            <section className="grid grid-cols-12 gap-4" data-testid="pulse-top-strip">
              <div className="col-span-12 xl:col-span-4"><BusinessHealthCard health={data.health} /></div>
              <div className="col-span-6 xl:col-span-2"><MetricCard metric={data.metrics?.net_sales} testid="pulse-metric-net-sales" /></div>
              <div className="col-span-6 xl:col-span-2"><MetricCard metric={data.metrics?.orders} testid="pulse-metric-orders" /></div>
              <div className="col-span-6 xl:col-span-2"><MetricCard metric={data.metrics?.aov} testid="pulse-metric-aov" /></div>
              <div className="col-span-6 xl:col-span-2"><MetricCard metric={data.metrics?.highest_order} testid="pulse-metric-highest" /></div>
            </section>

            {/* Main visual + insights */}
            <section className="grid grid-cols-12 gap-4">
              <div className="col-span-12 xl:col-span-9"><SalesTrendCard trend={data.sales_trend} /></div>
              <div className="col-span-12 xl:col-span-3"><KeyInsightsPanel insights={data.key_insights} /></div>
            </section>

            {/* What You Should Fix First — full-width prominent priority card */}
            <WhatYouShouldFixFirstCard insights={data.key_insights} />

            {/* Signal grid (single-period, click to expand). Reads
                ?signal= and ?from= for cross-link from Discounts FTF. */}
            <ExpandableSignalGrid
              signals={data.signals}
              dateRange={data.period}
              autoExpandKey={searchParams.get('signal')}
              contextFrom={searchParams.get('from')}
              onClearContext={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('from');
                next.delete('signal');
                setSearchParams(next, { replace: true });
              }}
            />

            {/* Long-Term signals — only when ≥3 months of history exist */}
            <LongTermSignalsSection signals={data.long_term_signals} />

            {/* Top Items This Period — period-over-period qty delta strip */}
            <section className="grid grid-cols-12 gap-4">
              <div className="col-span-12 lg:col-span-4">
                <TopItemsThisPeriodCard
                  outletId={null}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                />
              </div>
            </section>

            {/* Bottom analytics */}
            <section className="grid grid-cols-12 gap-4">
              <div className="col-span-12 lg:col-span-3"><DonutCard title="Revenue Composition" data={data.revenue_composition} palette={DONUT_PALETTE} testid="pulse-revenue-composition" /></div>
              <div className="col-span-12 lg:col-span-3">
                <DonutCard
                  title="Payment Mix"
                  data={data.payment_mix}
                  colorMap={PAYMENT_COLOR_MAP}
                  palette={DONUT_PALETTE}
                  testid="pulse-payment-mix"
                  showAllRows
                  showAbsValue
                  headerExtra={data.payment_mix_meta?.has_unclassified ? (
                    <span
                      className="px-1.5 py-0.5 rounded text-[9px] font-mono uppercase border bg-amber-500/15 text-amber-300 border-amber-500/40"
                      title={`₹${Number(data.payment_mix_meta.unclassified_amount).toLocaleString('en-IN')} not bucketed — review (mode, partner) tuples upstream`}
                      data-testid="pulse-payment-mix-unclassified-warning"
                    >
                      ⚠ Unclassified ₹{Number(data.payment_mix_meta.unclassified_amount).toLocaleString('en-IN')}
                    </span>
                  ) : null}
                  footnote={(() => {
                    const upi = (data.payment_mix || []).find((p) => p.label === 'UPI');
                    if (!upi || upi.delta_pct === undefined) return null;
                    const sign = upi.delta_pct >= 0 ? '↑' : '↓';
                    return `UPI share ${sign} ${Math.abs(upi.delta_pct)}% vs prior`;
                  })()}
                />
              </div>
              <div className="col-span-12 lg:col-span-3"><RevenueBreakdownCard data={data.revenue_breakdown} /></div>
              <div className="col-span-12 lg:col-span-3"><DayOfWeekCard data={data.day_of_week} /></div>
            </section>

            <DataConfidenceFooter confidence={data.data_confidence} />
          </>
        )}
      </div>
    </div>
  );
}
